import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkPlus, Highlighter, Redo2, Undo2 } from 'lucide-react';
import { savableWord, sentenceAround } from 'modules/vocabulary/selection';
import {
  HIGHLIGHT_CLASS,
  highlightMarks,
  highlightRange,
  offsetsToRange,
  rangeToOffsets,
  removeHighlight,
  setHighlightColor,
  setHighlightNote,
  type HighlightColor,
} from './annotate';
import { HighlightPanel } from './HighlightPanel';

export interface SavedHighlight {
  id: string;
  start: number;
  end: number;
  color: HighlightColor;
  note: string;
}

type Point = { top: number; left: number };

interface Props {
  /** Changes whenever the passage HTML inside changes (another page or part). */
  contentKey: string;
  highlights: SavedHighlight[];
  onChange: (update: (prev: SavedHighlight[]) => SavedHighlight[]) => void;
  /** Offers "Save word" next to Highlight when one to four words are selected (IELTS/SAT vocabulary). */
  onSaveWord?: (word: string, context: string) => void;
  children: React.ReactNode;
}

const PANEL_WIDTH = 300;
const BUTTON_WIDTH = 110;
// Wait for the selection to settle so the button does not chase the pointer while dragging.
const SELECTION_SETTLE_MS = 200;

/** `rect` (viewport) relative to `wrap`, which scrolls with the passage. */
function pointIn(wrap: HTMLElement | null, rect: DOMRect): Point | null {
  if (!wrap) return null;
  const box = wrap.getBoundingClientRect();
  return { top: rect.bottom - box.top, left: rect.left - box.left };
}

/**
 * Select passage text, press Highlight, then recolour it, add a note or delete
 * it. The passage HTML is re-rendered on every page or part switch, so each
 * highlight is kept as text offsets and put back onto the fresh HTML.
 * Only wrap HTML that React does not render itself (RenderedHtml).
 */
export function PassageHighlighter({ contentKey, highlights, onChange, onSaveWord, children }: Props) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const savedRange = useRef<Range | null>(null);
  const [selectionPoint, setSelectionPoint] = useState<Point | null>(null);
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [panelPoint, setPanelPoint] = useState<Point | null>(null);
  // Undo/redo per passage: snapshots of the highlight list before each create, recolour or delete.
  const history = useRef(new Map<string, { past: SavedHighlight[][]; future: SavedHighlight[][] }>());
  const [, setHistoryVersion] = useState(0);
  const stacks = () => {
    let entry = history.current.get(contentKey);
    if (!entry) history.current.set(contentKey, (entry = { past: [], future: [] }));
    return entry;
  };
  const commit = (update: (prev: SavedHighlight[]) => SavedHighlight[]) => {
    const entry = stacks();
    entry.past.push(highlights);
    entry.future = [];
    setHistoryVersion((v) => v + 1);
    onChange(update);
  };
  const travel = (from: 'past' | 'future') => {
    const entry = stacks();
    const target = entry[from].pop();
    if (!target) return;
    entry[from === 'past' ? 'future' : 'past'].push(highlights);
    setHistoryVersion((v) => v + 1);
    closePanel();
    onChange(() => target);
  };
  const undo = () => travel('past');
  const redo = () => travel('future');

  const closePanel = useCallback(() => {
    rootRef.current?.querySelectorAll(`.${HIGHLIGHT_CLASS}[data-active]`).forEach((m) => m.removeAttribute('data-active'));
    setActiveId(null);
  }, []);

  const openPanel = (id: string) => {
    const root = rootRef.current;
    const marks = root ? highlightMarks(root, id) : [];
    if (!root || !marks.length) return;
    root.querySelectorAll(`.${HIGHLIGHT_CLASS}[data-active]`).forEach((m) => m.removeAttribute('data-active'));
    marks.forEach((m) => (m.dataset.active = 'true'));
    const point = pointIn(wrapRef.current, marks[marks.length - 1].getBoundingClientRect());
    const width = wrapRef.current?.clientWidth ?? PANEL_WIDTH;
    setPanelPoint(point && { top: point.top + 8, left: Math.max(0, Math.min(point.left, width - PANEL_WIDTH)) });
    setActiveId(id);
  };

  // Make the marks match the list: put saved highlights back after the passage HTML is written
  // (RenderedHtml writes it in its own effect, which runs before this parent effect), and drop or
  // recolour marks an undo or redo changed.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const kept = new Set(highlights.map((h) => h.id));
    const shown = new Set(Array.from(root.querySelectorAll<HTMLElement>(`.${HIGHLIGHT_CLASS}`)).map((m) => m.dataset.hl ?? ''));
    shown.forEach((id) => {
      if (id && !kept.has(id)) removeHighlight(root, id);
    });
    for (const saved of highlights) {
      if (highlightMarks(root, saved.id).length) {
        setHighlightColor(root, saved.id, saved.color);
        setHighlightNote(root, saved.id, saved.note);
        continue;
      }
      const range = offsetsToRange(root, saved.start, saved.end);
      if (range && highlightRange(root, range, saved.id, saved.color) && saved.note) {
        setHighlightNote(root, saved.id, saved.note);
      }
    }
  }, [contentKey, highlights]);

  // Ctrl/Cmd+Z undoes, Ctrl/Cmd+Shift+Z or Ctrl+Y redoes, except while typing.
  const keyHandler = useRef<(e: KeyboardEvent) => void>(() => undefined);
  keyHandler.current = (e: KeyboardEvent) => {
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
    const target = e.target;
    if (target instanceof Element && target.closest('input, textarea, [contenteditable="true"]')) return;
    const key = e.key.toLowerCase();
    const entry = history.current.get(contentKey);
    if (key === 'z' && !e.shiftKey && entry?.past.length) {
      e.preventDefault();
      undo();
    } else if (((key === 'z' && e.shiftKey) || key === 'y') && entry?.future.length) {
      e.preventDefault();
      redo();
    }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => keyHandler.current(e);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // A panel belongs to the passage it was opened on.
  useEffect(() => closePanel(), [contentKey, closePanel]);

  // Offer the Highlight button once a selection inside the passage settles.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onSelectionChange = () => {
      clearTimeout(timer);
      const selection = window.getSelection();
      const root = rootRef.current;
      const range = selection && selection.rangeCount && !selection.isCollapsed ? selection.getRangeAt(0) : null;
      if (!root || !range || !root.contains(range.commonAncestorContainer)) {
        savedRange.current = null;
        setSelectionPoint(null);
        setSelectedWord(null);
        return;
      }
      savedRange.current = range.cloneRange();
      setSelectedWord(savableWord(range.toString()));
      timer = setTimeout(() => {
        // A new selection replaces the open panel.
        closePanel();
        const rects = typeof range.getClientRects === 'function' ? range.getClientRects() : null;
        const last = rects && rects.length ? rects[rects.length - 1] : null;
        const point = last ? pointIn(wrapRef.current, last) : null;
        const width = wrapRef.current?.clientWidth ?? BUTTON_WIDTH;
        setSelectionPoint(
          point
            ? { top: point.top + 6, left: Math.max(0, Math.min(point.left + last!.width - BUTTON_WIDTH / 2, width - BUTTON_WIDTH)) }
            : { top: 0, left: 0 },
        );
      }, SELECTION_SETTLE_MS);
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, [closePanel]);

  const createHighlight = () => {
    const root = rootRef.current;
    const range = savedRange.current;
    if (!root || !range) return;
    // Offsets first: wrapping splits the text nodes the range points at.
    const offsets = rangeToOffsets(root, range);
    const id = `hl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    if (!offsets || !highlightRange(root, range, id, 'yellow')) return;
    savedRange.current = null;
    setSelectionPoint(null);
    window.getSelection()?.removeAllRanges();
    commit((prev) => [...prev, { id, ...offsets, color: 'yellow', note: '' }]);
    openPanel(id);
  };

  const saveWord = () => {
    const range = savedRange.current;
    if (!onSaveWord || !range || !selectedWord) return;
    const block = (range.startContainer.parentElement?.closest('p, li, td, div') ?? rootRef.current) as HTMLElement | null;
    onSaveWord(selectedWord, sentenceAround(block?.textContent ?? '', selectedWord));
    savedRange.current = null;
    setSelectionPoint(null);
    setSelectedWord(null);
    window.getSelection()?.removeAllRanges();
  };

  const onPassageClick = (e: React.MouseEvent) => {
    const mark = (e.target as HTMLElement).closest(`.${HIGHLIGHT_CLASS}`) as HTMLElement | null;
    if (mark?.dataset.hl && window.getSelection()?.isCollapsed !== false) openPanel(mark.dataset.hl);
  };

  const active = activeId ? highlights.find((h) => h.id === activeId) : undefined;
  const update = (id: string, patch: Partial<SavedHighlight>) =>
    onChange((prev) => prev.map((h) => (h.id === id ? { ...h, ...patch } : h)));

  const entry = history.current.get(contentKey);
  const canUndo = Boolean(entry?.past.length);
  const canRedo = Boolean(entry?.future.length);

  return (
    <div ref={wrapRef} className="relative">
      {(canUndo || canRedo) && (
        // Zero height: the buttons float over the passage corner without pushing the text down.
        <div className="sticky top-0 z-10 flex h-0 justify-end gap-1">
          <button
            type="button"
            onClick={undo}
            disabled={!canUndo}
            aria-label={t('exam.reading.undo')}
            title={t('exam.reading.undo')}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-40"
          >
            <Undo2 className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={!canRedo}
            aria-label={t('exam.reading.redo')}
            title={t('exam.reading.redo')}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-40"
          >
            <Redo2 className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}
      <div ref={rootRef} onClick={onPassageClick}>
        {children}
      </div>

      {selectionPoint && (
        <button
          type="button"
          // Keep the selection: pressing the button must not clear it.
          onMouseDown={(e) => e.preventDefault()}
          onClick={createHighlight}
          style={{ top: selectionPoint.top, left: selectionPoint.left, width: BUTTON_WIDTH }}
          className="absolute z-20 flex h-9 items-center justify-center gap-1.5 rounded-lg bg-[#0F172A] font-sans text-[13px] font-semibold text-white shadow-[0_8px_20px_rgba(15,23,42,0.25)] hover:bg-[#1E293B]"
        >
          <Highlighter className="h-4 w-4 text-[#FDE68A]" aria-hidden />
          {t('exam.sat.highlight')}
        </button>
      )}

      {selectionPoint && onSaveWord && selectedWord && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={saveWord}
          style={{ top: selectionPoint.top + 42, left: selectionPoint.left, width: BUTTON_WIDTH }}
          className="absolute z-20 flex h-9 items-center justify-center gap-1.5 rounded-lg border border-[#0F172A]/10 bg-white font-sans text-[13px] font-semibold text-[#0F172A] shadow-[0_8px_20px_rgba(15,23,42,0.18)] hover:bg-[#F1F5F9]"
        >
          <BookmarkPlus className="h-4 w-4 text-[#2563EB]" aria-hidden />
          {t('vocab.save_word')}
        </button>
      )}

      {active && rootRef.current && (
        <HighlightPanel
          key={active.id}
          color={active.color}
          note={active.note}
          position={panelPoint}
          onColor={(color) => {
            if (color === active.color) return;
            if (rootRef.current) setHighlightColor(rootRef.current, active.id, color);
            commit((prev) => prev.map((h) => (h.id === active.id ? { ...h, color } : h)));
          }}
          onNote={(note) => {
            if (rootRef.current) setHighlightNote(rootRef.current, active.id, note);
            update(active.id, { note });
          }}
          onDelete={() => {
            if (rootRef.current) removeHighlight(rootRef.current, active.id);
            commit((prev) => prev.filter((h) => h.id !== active.id));
            setActiveId(null);
          }}
          onClose={closePanel}
        />
      )}
    </div>
  );
}

export default PassageHighlighter;
